import React, { useRef, useState, useEffect } from 'react';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { AppBar, Dialog, IconButton, Typography, Toolbar } from '@mui/material';

// local imports
import { ButtonStyle, Module } from 'types/AgilityTypes';
import { callApi } from 'services/apiWrapper';
import { logClientException } from 'appinsights/clientAppInsights';
import PleaseWait from 'components/common/PleaseWait';
import CommonButton from 'components/common/CommonButton';
import AIDisclaimer from 'components/common/AIDisclaimer';

export type RealityCoachIntegratorFields = {
  launchButtonText: string;
  launchButtonStyle: ButtonStyle;
  hideButton: string;
  launchButtonStyle_ValueField: string;
  openIniFrame: string;
  iFrameTitleBarText?: string;
  aiDisclaimerLinkText?: string;
  aiDisclaimer?: string;
};

// check iframe URL every 1000ms to determine when to close the dialog based on URL changes within the iframe
const IFRAME_URL_CHECK_INTERVAL_MS = 1000;
const LIFT_WITH_BOYSTOWN_HOSTNAMES = [
  'liftwithboystown.org',
  'tst.liftwithboystown.org',
  'staging.liftwithboystown.org',
  'dev.liftwithboystown.org',
];

const RealityCoachIntegrator = ({
  module,
}: {
  module: Module<RealityCoachIntegratorFields>;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [coachUrl, setCoachUrl] = useState('');
  const fields: RealityCoachIntegratorFields = module?.fields || {
    launchButtonText: 'Start Reality Coach',
    launchButtonStyle: {} as ButtonStyle,
    launchButtonStyle_ValueField: '',
    openIniFrame: 'false',
    iFrameTitleBarText: 'Reality Coach',
  };
  const launchButtonStyle = fields?.launchButtonStyle || {};
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const launchRealityCoach = async () => {
    try {
      setIsLoading(true);
      const data = await callApi('/api/reality-coach/launch', 'POST', null, {
        timeout: 20000,
      });
      if (data && data.redirectUrl) {
        if (fields.openIniFrame === 'true') {
          // Open in an iframe if specified
          setCoachUrl(data.redirectUrl);
          setViewDialogOpen(true);
          return;
        } else {
          window.open(data.redirectUrl, '_blank', 'noopener,noreferrer');
          return;
        }
      }
      throw new Error('Unexpected response format from Reality Coach launch');
    } catch (error) {
      console.error('Error launching Reality Coach:', error);
      logClientException(error);
      enqueueSnackbar('Reality Coach launch failed. Please try again.', {
        variant: 'error',
        action: (key) => (
          <button onClick={() => closeSnackbar(key)}>Dismiss</button>
        ),
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Function to check URL changes
    const checkUrlChange = () => {
      const iframe = iframeRef.current;
      if (!iframe) {
        return;
      }
      try {
        const currentUrl = iframe.contentWindow?.location.href;
        if (currentUrl) {
          // Check for specific URL patterns to determine when to close the dialog
          const host = new URL(currentUrl).host;
          if (
            LIFT_WITH_BOYSTOWN_HOSTNAMES.some((hostname) =>
              host.toLowerCase().endsWith(hostname),
            )
          ) {
            setViewDialogOpen(false);
            setCoachUrl('');
          }
        }
      } catch (err) {
        // Cross-origin access will throw here - will get this while RC is running
        // but we should be able to access the URL once it navigates to a liftwithboystown.org page, which is our signal to close the dialog
        if (err instanceof DOMException && err.name === 'SecurityError') {
          // This is expected when trying to access cross-origin iframes before they navigate to a same-origin URL
          return;
        }
        console.warn('Cannot access iframe content:', err);
      }
    };

    if (!viewDialogOpen || !coachUrl) {
      return;
    }

    // Poll interval to check for URL changes in the iframe
    const interval = setInterval(checkUrlChange, IFRAME_URL_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [coachUrl, viewDialogOpen]);

  const handleDialogClose = () => {
    setViewDialogOpen(false);
    setCoachUrl('');
  };

  return (
    <div className="flex justify-center items-center h-full m-4">
      <PleaseWait isLoading={isLoading} />
      <div className="flex flex-col items-center">
        <CommonButton
          text={fields?.launchButtonText}
          buttonStyle={launchButtonStyle}
          onClick={launchRealityCoach}
          dataTestId="reality-coach-launch-button"
          id="reality-coach-launch-button"
          style={{
            visibility: fields.hideButton === 'true' ? 'collapse' : 'visible',
          }}
        />

        {fields.aiDisclaimerLinkText && (
          <div className="text-xs text-gray-600 mt-3 text-center md:flex md:justify-center md:items-center">
            <AIDisclaimer
              linkText={fields.aiDisclaimerLinkText}
              disclaimer={fields.aiDisclaimer || ''}
            />
          </div>
        )}

        <Dialog fullScreen open={viewDialogOpen} onClose={handleDialogClose}>
          <AppBar sx={{ position: 'relative' }}>
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleDialogClose}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
              <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                {fields.iFrameTitleBarText}
              </Typography>
            </Toolbar>
          </AppBar>
          {coachUrl && (
            <iframe
              ref={iframeRef}
              src={coachUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Reality Coach"
              allow="camera;microphone;"
            />
          )}
        </Dialog>
      </div>
    </div>
  );
};

export default RealityCoachIntegrator;
